/**
 * Utility functions for traversing and searching outline tree structures
 */

interface NodeWithParent {
    node: OutlineNode;
    parent?: OutlineNode;
}

/**
 * Find a node by ID in the outline tree
 */
export function findNodeById(nodes: OutlineNode[], targetId: string, parent?: OutlineNode): NodeWithParent | undefined {
    for (const node of nodes) {
        if (node.id === targetId) {
            return { node, parent };
        }
        if (node.children?.length > 0) {
            const found = findNodeById(node.children, targetId, node);
            if (found) {
                return found;
            }
        }
    }
}

/**
 * Find the node containing a specific passage ID
 */
function findNodeContainingPassage(nodes: OutlineNode[], passageId: string, parent?: OutlineNode): NodeWithParent | undefined {
    for (const node of nodes) {
        if (node.sectionIds?.includes(passageId)) {
            return { node, parent };
        }
        if (node.children?.length > 0) {
            const found = findNodeContainingPassage(node.children, passageId, node);
            if (found) {
                return found;
            }
        }
    }
}

/**
 * Find which direct child of a node contains a specific passage
 * Returns the direct child, not the descendant
 */
export function findChildContainingPassage(node: OutlineNode, passageId: string): OutlineNode | undefined {
    if (isLeafNode(node)) {
        return;
    }

    for (const child of node.children) {
        // Check if this child contains the passage
        if (child.sectionIds?.includes(passageId)) {
            return child;
        }
        // Check if any descendant contains the passage
        const descendant = findChildContainingPassage(child, passageId);
        if (descendant) {
            return child; // Return the direct child, not the descendant
        }
    }
}

/**
 * Check if a node or any of its descendants contains a specific passage
 */
export function containsPassage(node: OutlineNode, passageId: string): boolean {
    if (node.sectionIds?.includes(passageId)) {
        return true;
    }
    if (node.children) {
        return node.children.some(child => containsPassage(child, passageId));
    }
    return false;
}

/**
 * Check if a node is a leaf node (has no children)
 */
export function isLeafNode(node: OutlineNode): boolean {
    return !node.children || node.children.length === 0;
}

/**
 * Returns true if target is the same as ancestor or a descendant of it
 */
export function isDescendantOf(target: OutlineNode, ancestor: OutlineNode): boolean {
    if (target.id === ancestor.id) return true;
    if (!ancestor.children || ancestor.children.length === 0) return false;
    for (const child of ancestor.children) {
        if (isDescendantOf(target, child)) return true;
    }
    return false;
}

/**
 * Find the path from root structure to a node id, inclusive
 */
export function findPathToNode(structure: OutlineNode[], targetId: string): OutlineNode[] {
    const path: OutlineNode[] = [];
    const dfs = (nodes: OutlineNode[], acc: OutlineNode[]): boolean => {
        for (const n of nodes) {
            const next = [...acc, n];
            if (n.id === targetId) { path.push(...next); return true; }
            if (n.children && dfs(n.children, next)) return true;
        }
        return false;
    };
    dfs(structure, []);
    return path;
}

/**
 * Find the outline containing a specific passage and return context information
 */
export function findOutlineContext(outlines: Outline[], passageId: string): { outline: Outline; initialNode: OutlineNode; parentNode?: OutlineNode;} | undefined {
    for (const outline of outlines) {
        const result = findNodeContainingPassage(outline.structure, passageId);
        if (result) {
            return { outline, initialNode: result.node, parentNode: result.parent };
        }
    }
}

/**
 * Create a virtual root node for displaying all root-level nodes
 */
export function createVirtualRootNode(outline: Outline): OutlineNode {
    return {
        id: outline.id,
        title: outline.book,
        ref: '',
        sectionIds: [],
        children: outline.structure,
        numVerses: outline.numVerses || 0
    };
}

/**
 * Find the first leaf node in a tree and return its first section ID
 */
export function findFirstLeafSectionId(node: OutlineNode): string | undefined {
    // If this is a leaf node, return its first section ID
    if (isLeafNode(node) && node.sectionIds?.length > 0) {
        return node.sectionIds[0];
    }
    // Otherwise, recursively check children
    if (node.children) {
        for (const child of node.children) {
            const result = findFirstLeafSectionId(child);
            if (result) {
                return result;
            }
        }
    }
}

/**
 * Find the first section ID in an outline
 */
export function findFirstSectionInOutline(outline: Outline): string | undefined {
    for (const rootNode of outline.structure) {
        const firstSectionId = findFirstLeafSectionId(rootNode);
        if (firstSectionId) {
            return firstSectionId;
        }
    }
}

/**
 * Build a set of node IDs that should be visible in a filtered outline view.
 * Returns nodes that contain the passage (ancestors) and all siblings of nodes in the path to the passage.
 *
 * @param nodes - Array of outline nodes to search
 * @param passageId - The passage ID to find
 * @param visibleIds - Set to accumulate visible node IDs (used for recursion)
 * @returns Set of node IDs that should be visible
 */
export function buildVisibleNodeIds(nodes: OutlineNode[], passageId: string, visibleIds: Set<string> = new Set()): Set<string> {
    for (const node of nodes) {
        const nodeContainsPassage = containsPassage(node, passageId);

        if (nodeContainsPassage) {
            // This node is in the path - add it and all its siblings
            for (const sibling of nodes) {
                visibleIds.add(sibling.id);
            }

            // Recurse into children to find the path deeper
            if (node.children) {
                buildVisibleNodeIds(node.children, passageId, visibleIds);
            }
            break; // Only one node at this level can contain the passage
        }
    }

    return visibleIds;
}


/**
 * Collect all unique forms and genres from visible outline nodes
 * Returns an array of {form, subgenre, genre, label} objects sorted by genre, subgenre, then form
 */
export function collectUniqueFormsFromOutline(
    nodes: OutlineNode[],
    passageMap: Map<string, Passage>,
    visibleNodeIds?: Set<string>
): Array<{form: string | undefined, subgenre: string | undefined, genre: string | undefined, label: string}> {
    const formsMap = new Map<string, {form: string | undefined, subgenre: string | undefined, genre: string | undefined, label: string}>();

    function traverse(node: OutlineNode) {
        // Skip if not visible
        if (visibleNodeIds && !visibleNodeIds.has(node.id)) {
            return;
        }

        // Get the first passage for this node
        const firstSectionId = findFirstLeafSectionId(node);
        if (firstSectionId) {
            const passage = passageMap.get(firstSectionId);
            if (passage) {
                const form = passage.form;
                const subgenre = passage.subgenre;
                const genre = passage.genre;

                // Create a unique key from the combination
                const key = `${genre || 'Unknown'}|${subgenre || ''}|${form || ''}`;

                // Create label showing the most specific level
                const label = form || subgenre || genre || 'Unknown';

                // Use key to avoid duplicates
                if (!formsMap.has(key)) {
                    formsMap.set(key, { form, subgenre, genre, label });
                }
            }
        }

        // Recurse into children
        if (node.children) {
            node.children.forEach(traverse);
        }
    }

    nodes.forEach(traverse);

    // Define genre order for consistent sorting
    const genreOrder: Record<string, number> = {
        'Narrative': 1,
        'Poetry': 2,
        'Wisdom': 3,
        'Prophecy': 4,
        'Epistle': 5,
        'Law': 6
    };

    // Convert to array and sort by genre (custom order), then subgenre (alphabetically), then form (alphabetically)
    return Array.from(formsMap.values()).sort((a, b) => {
        // Sort by genre using custom order
        const genreOrderA = genreOrder[a.genre || ''] || 999;
        const genreOrderB = genreOrder[b.genre || ''] || 999;
        const genreCompare = genreOrderA - genreOrderB;
        if (genreCompare !== 0) return genreCompare;

        // Then by subgenre alphabetically
        const subgenreCompare = (a.subgenre || 'ZZZ').localeCompare(b.subgenre || 'ZZZ');
        if (subgenreCompare !== 0) return subgenreCompare;

        // Finally by form alphabetically
        return (a.form || 'ZZZ').localeCompare(b.form || 'ZZZ');
    });
}